import React from 'react';
import { TooltipText } from '@components/TooltipText';
import { ColumnProps } from 'primereact/column';
import { timestampToFormattedDate } from '@utilities/date';
import { getEnumDisplayValue } from '@utilities';

// Reusable avatar color generator (dark-mode aware)
const generateAvatarColor = (name: string) => {
	const isDark = document.documentElement.classList.contains('dark');
	const code = (name || 'U').toUpperCase().charCodeAt(0);
	const hash = (code * 1234567) % 0xaaaaaa;
	const hex = `#${hash.toString(16).padStart(6, '0')}`;
	const r = parseInt(hex.substring(1, 3), 16);
	const g = parseInt(hex.substring(3, 5), 16);
	const b = parseInt(hex.substring(5, 7), 16);
	if (isDark) {
		return {
			bg: `rgba(${r}, ${g}, ${b}, 0.2)`,
			text: `rgb(${Math.floor(r * 0.3 + 255 * 0.7)}, ${Math.floor(g * 0.3 + 255 * 0.7)}, ${Math.floor(b * 0.3 + 255 * 0.7)})`
		};
	}
	return {
		bg: `rgb(${Math.floor(r * 0.25 + 255 * 0.75)}, ${Math.floor(g * 0.25 + 255 * 0.75)}, ${Math.floor(b * 0.25 + 255 * 0.75)})`,
		text: `rgb(${Math.floor(r * 0.8)}, ${Math.floor(g * 0.8)}, ${Math.floor(b * 0.8)})`
	};
};

// Status dot color by status key
const STATUS_DOT_COLORS: Record<string, string> = {
	NEW:         'var(--color-text-tertiary)',
	BUCKET:      'var(--color-text-tertiary)',
	UNQUALIFIED: 'var(--color-danger)',
	MQL:         'var(--color-info)',
	PQL:         'var(--color-info)',
	QL:          'var(--color-warning)',
	SQL:         'var(--color-success)'
};

const getStatusDotColor = (status: string): string =>
	STATUS_DOT_COLORS[status] ?? 'var(--color-text-tertiary)';

// Relative-day label helper
const relativeDay = (timestamp: number | string): string => {
	const date = new Date(timestamp);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffDays = Math.floor(diffMs / 86400000);
	if (diffDays === 0) return 'Today';
	if (diffDays === 1) return 'Yesterday';
	if (diffDays < 7) return `${diffDays}d ago`;
	return '';
};

// Follow-up date classification
const getFollowUpColor = (timestamp: number | string | null | undefined): string => {
	if (!timestamp) return 'text-skin-base';
	const date = new Date(timestamp);
	const now = new Date();
	const diffMs = date.getTime() - now.getTime();
	const diffDays = Math.floor(diffMs / 86400000);
	if (diffMs < 0) return 'text-skin-danger font-semibold'; // overdue
	if (diffDays <= 1) return 'text-skin-success'; // today or tomorrow
	return 'text-skin-base';
};

export const COLUMNS: ColumnProps[] = [
	// 1. Lead — 36px avatar + name (font-semibold) + email-or-phone faint second line
	{
		field: 'fullName',
		header: 'Lead',
		body: (lead: Record<string, any>) => {
			const name = lead.fullName || 'Unknown';
			const colors = generateAvatarColor(name);
			const secondary = lead.email || lead.phoneNumber || null;
			return (
				<div className="flex items-center gap-2.5 min-w-0">
					<div
						className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
						style={{ backgroundColor: colors.bg, color: colors.text }}
					>
						{name.charAt(0).toUpperCase()}
					</div>
					<div className="flex flex-col min-w-0">
						<span className="text-sm font-semibold text-skin-base leading-snug truncate">{name}</span>
						{secondary && (
							<span className="text-[12px] text-skin-faint truncate">{secondary}</span>
						)}
					</div>
				</div>
			);
		}
	},

	// 2. Contact — phone number tabular-nums
	{
		field: 'phoneNumber',
		header: 'Contact',
		body: (lead: Record<string, any>) => {
			if (!lead.phoneNumber) return <span className="text-[13px] text-skin-faint">—</span>;
			return (
				<span className="text-[13px] text-skin-base tabular-nums">{lead.phoneNumber}</span>
			);
		}
	},

	// 3. Status — dot-badge pill
	{
		field: 'status',
		header: 'Status',
		body: (lead: Record<string, any>) => {
			if (!lead.status) return <span className="text-sm text-skin-faint">—</span>;
			const label = getEnumDisplayValue(lead.status, LEAD_STATUSES) || lead.status;
			const dotColor = getStatusDotColor(lead.status);
			return (
				<span
					className="inline-flex items-center gap-2 bg-skin-card border border-skin-strong rounded-full px-3 py-1.5 text-[13px] font-semibold whitespace-nowrap text-skin-base"
					title={LEAD_STATUS_TOOLTIPS?.[lead.status as keyof typeof LEAD_STATUS_TOOLTIPS] ?? label}
				>
					<span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
					{label}
				</span>
			);
		}
	},

	// 4. Source — plain muted text
	{
		field: 'source',
		header: 'Source',
		body: (lead: Record<string, any>) => {
			if (!lead.source) return <span className="text-[13px] text-skin-faint">—</span>;
			const label = getEnumDisplayValue(lead.source, LEAD_SOURCES) || lead.source;
			return <span className="text-[13px] text-skin-muted">{label}</span>;
		}
	},

	// 5. Assigned to — tinted initials avatar 24px + name
	{
		field: 'assignedTo',
		header: 'Assigned to',
		body: (lead: Record<string, any>) => {
			const name: string | undefined = lead.assignedTo || lead.assignedUser || lead.addedByName;
			if (!name) return <span className="text-[13px] text-skin-faint">—</span>;
			const colors = generateAvatarColor(name);
			const initials = name
				.split(' ')
				.map(w => w[0])
				.join('')
				.toUpperCase()
				.slice(0, 2);
			return (
				<div className="flex items-center gap-1.5 min-w-0">
					<div
						className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold shrink-0"
						style={{ backgroundColor: colors.bg, color: colors.text }}
					>
						{initials}
					</div>
					<span className="text-[13px] text-skin-base truncate">{name}</span>
				</div>
			);
		}
	},

	// 6. Follow-up date — colored semantically: overdue red, today/upcoming green, else base; relative second line faint
	{
		field: 'followUpDate',
		header: 'Follow-up',
		body: (lead: Record<string, any>) => {
			const ts = lead.followUpDate || lead.followUp;
			if (!ts) return <span className="text-[13px] text-skin-faint">—</span>;
			const colorClass = getFollowUpColor(ts);
			const formatted = timestampToFormattedDate(ts, 'MMM d, yyyy');
			const relative = relativeDay(ts);
			return (
				<div className="flex flex-col gap-0.5">
					<span className={`text-[13px] tabular-nums ${colorClass}`}>{formatted}</span>
					{relative && (
						<span className="text-[11px] text-skin-faint">{relative}</span>
					)}
				</div>
			);
		}
	},

	// 7. Added — sortable; creation date + relative label
	{
		field: 'addedOn',
		header: 'Added',
		sortable: true,
		body: (lead: Record<string, any>) => {
			if (!lead.addedOn) return <span className="text-[13px] text-skin-faint">—</span>;
			const relative = relativeDay(lead.addedOn);
			return (
				<div className="flex flex-col gap-0.5">
					<span className="text-[13px] text-skin-base tabular-nums">
						{timestampToFormattedDate(lead.addedOn, 'MMM d, yyyy')}
					</span>
					{relative && (
						<span className="text-[11px] text-skin-faint">{relative}</span>
					)}
				</div>
			);
		}
	},

	// 8. Updated — sortable; last-updated date + relative label
	{
		field: 'updatedOn',
		header: 'Updated',
		sortable: true,
		body: (lead: Record<string, any>) => {
			if (!lead.updatedOn) return <span className="text-[13px] text-skin-faint">—</span>;
			const relative = relativeDay(lead.updatedOn);
			return (
				<div className="flex flex-col gap-0.5">
					<span className="text-[13px] text-skin-base tabular-nums">
						{timestampToFormattedDate(lead.updatedOn, 'MMM d, yyyy')}
					</span>
					{relative && (
						<span className="text-[11px] text-skin-faint">{relative}</span>
					)}
				</div>
			);
		}
	},

	// 9. Latest Remark — truncated tooltip
	{
		field: 'latestRemark',
		header: 'Remark',
		body: (lead: Record<string, any>) => (
			<div className="flex items-center gap-1.5 text-[13px] text-skin-muted">
				<TooltipText
					tooltipText={lead.latestRemark || '—'}
					charCount={30}
					tooltipClassName="max-w-sm whitespace-normal break-words"
				/>
			</div>
		)
	}
];

export const SORT_COLUMN_KEYS = {
	addedOn: 'ADDED_ON',
	updatedOn: 'UPDATED_ON'
};

export enum LEAD_SOURCES_KEYS {
	CUSTOMER_TOOL = 'CUSTOMER_TOOL',
	WEBSITE = 'WEBSITE',
	GOOGLE = 'GOOGLE',
	META = 'META',
	WALK_IN = 'WALK_IN',
	DIGITAL = 'DIGITAL',
	REFERRAL = 'REFERRAL',
	MODERNSPAACES = 'MODERNSPAACES'
}

export const LEAD_SOURCES = {
	[LEAD_SOURCES_KEYS.CUSTOMER_TOOL]: {
		key: LEAD_SOURCES_KEYS.CUSTOMER_TOOL,
		value: LEAD_SOURCES_KEYS.CUSTOMER_TOOL,
		label: 'Customer Tool'
	},
	[LEAD_SOURCES_KEYS.WEBSITE]: {
		key: LEAD_SOURCES_KEYS.WEBSITE,
		value: LEAD_SOURCES_KEYS.WEBSITE,
		label: 'Website'
	},
	[LEAD_SOURCES_KEYS.GOOGLE]: {
		key: LEAD_SOURCES_KEYS.GOOGLE,
		value: LEAD_SOURCES_KEYS.GOOGLE,
		label: 'Google'
	},
	[LEAD_SOURCES_KEYS.META]: {
		key: LEAD_SOURCES_KEYS.META,
		value: LEAD_SOURCES_KEYS.META,
		label: 'Meta'
	},
	[LEAD_SOURCES_KEYS.WALK_IN]: {
		key: LEAD_SOURCES_KEYS.WALK_IN,
		value: LEAD_SOURCES_KEYS.WALK_IN,
		label: 'Walk In'
	},
	[LEAD_SOURCES_KEYS.DIGITAL]: {
		key: LEAD_SOURCES_KEYS.DIGITAL,
		value: LEAD_SOURCES_KEYS.DIGITAL,
		label: 'Digital'
	},
	[LEAD_SOURCES_KEYS.REFERRAL]: {
		key: LEAD_SOURCES_KEYS.REFERRAL,
		value: LEAD_SOURCES_KEYS.REFERRAL,
		label: 'Referral'
	},
	[LEAD_SOURCES_KEYS.MODERNSPAACES]: {
		key: LEAD_SOURCES_KEYS.MODERNSPAACES,
		value: LEAD_SOURCES_KEYS.MODERNSPAACES,
		label: 'Modern Spaces'
	}
};

export enum PROJECT_SCOPES_KEYS {
	TURNKEY = 'TURNKEY',
	KITCHEN = 'KITCHEN',
	FURNISHINGS = 'FURNISHINGS',
	MODULARS = 'MODULARS'
}

export const PROJECT_SCOPES = {
	[PROJECT_SCOPES_KEYS.TURNKEY]: {
		key: PROJECT_SCOPES_KEYS.TURNKEY,
		value: PROJECT_SCOPES_KEYS.TURNKEY,
		label: 'Turnkey'
	},
	[PROJECT_SCOPES_KEYS.KITCHEN]: {
		key: PROJECT_SCOPES_KEYS.KITCHEN,
		value: PROJECT_SCOPES_KEYS.KITCHEN,
		label: 'Kitchen'
	},
	[PROJECT_SCOPES_KEYS.FURNISHINGS]: {
		key: PROJECT_SCOPES_KEYS.FURNISHINGS,
		value: PROJECT_SCOPES_KEYS.FURNISHINGS,
		label: 'Furnishings'
	},
	[PROJECT_SCOPES_KEYS.MODULARS]: {
		key: PROJECT_SCOPES_KEYS.MODULARS,
		value: PROJECT_SCOPES_KEYS.MODULARS,
		label: 'Modulars'
	}
};

export enum LEAD_STATUSES_KEYS {
	NEW = 'NEW',
	BUCKET = 'BUCKET',
	UNQUALIFIED = 'UNQUALIFIED',
	MQL = 'MQL',
	PQL = 'PQL',
	QL = 'QL',
	SQL = 'SQL'
}

export const LEAD_STATUSES = {
	[LEAD_STATUSES_KEYS.NEW]: {
		key: LEAD_STATUSES_KEYS.NEW,
		value: LEAD_STATUSES_KEYS.NEW,
		label: 'New'
	},
	[LEAD_STATUSES_KEYS.BUCKET]: {
		key: LEAD_STATUSES_KEYS.BUCKET,
		value: LEAD_STATUSES_KEYS.BUCKET,
		label: 'Bucket'
	},
	[LEAD_STATUSES_KEYS.UNQUALIFIED]: {
		key: LEAD_STATUSES_KEYS.UNQUALIFIED,
		value: LEAD_STATUSES_KEYS.UNQUALIFIED,
		label: 'Unqualified'
	},
	[LEAD_STATUSES_KEYS.MQL]: {
		key: LEAD_STATUSES_KEYS.MQL,
		value: LEAD_STATUSES_KEYS.MQL,
		label: 'MQL'
	},
	[LEAD_STATUSES_KEYS.PQL]: {
		key: LEAD_STATUSES_KEYS.PQL,
		value: LEAD_STATUSES_KEYS.PQL,
		label: 'PQL'
	},
	[LEAD_STATUSES_KEYS.QL]: {
		key: LEAD_STATUSES_KEYS.QL,
		value: LEAD_STATUSES_KEYS.QL,
		label: 'QL'
	},
	[LEAD_STATUSES_KEYS.SQL]: {
		key: LEAD_STATUSES_KEYS.SQL,
		value: LEAD_STATUSES_KEYS.SQL,
		label: 'SQL'
	}
};

/** Tooltips shown on hover for MQL, PQL, QL, SQL only */
export const LEAD_STATUS_TOOLTIPS: Partial<Record<LEAD_STATUSES_KEYS, string>> = {
	[LEAD_STATUSES_KEYS.MQL]: 'Marketing Qualified',
	[LEAD_STATUSES_KEYS.PQL]: 'Product Qualified',
	[LEAD_STATUSES_KEYS.QL]: 'Qualified Lead',
	[LEAD_STATUSES_KEYS.SQL]: 'Sales Qualified'
};

export enum LEAD_SUB_STATUSES_KEYS {
	COLD = 'COLD',
	WARM = 'WARM',
	HOT = 'HOT',
	DEAD = 'DEAD',
	RNR = 'RNR'
}

export const LEAD_SUB_STATUSES = {
	[LEAD_SUB_STATUSES_KEYS.COLD]: {
		key: LEAD_SUB_STATUSES_KEYS.COLD,
		value: LEAD_SUB_STATUSES_KEYS.COLD,
		label: 'Cold'
	},
	[LEAD_SUB_STATUSES_KEYS.WARM]: {
		key: LEAD_SUB_STATUSES_KEYS.WARM,
		value: LEAD_SUB_STATUSES_KEYS.WARM,
		label: 'Warm'
	},
	[LEAD_SUB_STATUSES_KEYS.HOT]: {
		key: LEAD_SUB_STATUSES_KEYS.HOT,
		value: LEAD_SUB_STATUSES_KEYS.HOT,
		label: 'Hot'
	},
	[LEAD_SUB_STATUSES_KEYS.DEAD]: {
		key: LEAD_SUB_STATUSES_KEYS.DEAD,
		value: LEAD_SUB_STATUSES_KEYS.DEAD,
		label: 'Dead'
	},
	[LEAD_SUB_STATUSES_KEYS.RNR]: {
		key: LEAD_SUB_STATUSES_KEYS.RNR,
		value: LEAD_SUB_STATUSES_KEYS.RNR,
		label: 'RNR'
	}
};

export enum LEAD_FILTERS_KEYS {
	LEAD_NAME = 'leadName',
	LEAD_STATUS = 'leadStatus',
	LEAD_SUB_STATUS = 'subStatus',
	FOLLOW_UP_DATE = 'followUpDate',
	UPDATED_ON = 'updatedOn',
	ADDED_ON = 'addedOn',
	ADDED_BY = 'addedBy',
	SOURCE = 'source'
}

export const LEAD_FILTERS: any = {
	[LEAD_FILTERS_KEYS.LEAD_NAME]: {
		key: LEAD_FILTERS_KEYS.LEAD_NAME,
		label: 'Lead Name',
		type: 'Autocomplete'
	},
	[LEAD_FILTERS_KEYS.LEAD_STATUS]: {
		key: LEAD_FILTERS_KEYS.LEAD_STATUS,
		label: 'Status',
		type: 'Array',
		item: 'Object'
	},
	[LEAD_FILTERS_KEYS.LEAD_SUB_STATUS]: {
		key: LEAD_FILTERS_KEYS.LEAD_SUB_STATUS,
		label: 'Temp',
		type: 'Array',
		item: 'Object'
	},
	[LEAD_FILTERS_KEYS.UPDATED_ON]: {
		key: LEAD_FILTERS_KEYS.UPDATED_ON,
		label: 'Updated On',
		type: 'DateRange'
	},
	[LEAD_FILTERS_KEYS.ADDED_ON]: {
		key: LEAD_FILTERS_KEYS.ADDED_ON,
		label: 'Added On',
		type: 'DateRange'
	},
	[LEAD_FILTERS_KEYS.ADDED_BY]: {
		key: LEAD_FILTERS_KEYS.ADDED_BY,
		label: 'Added By',
		type: 'Autocomplete'
	},
	[LEAD_FILTERS_KEYS.SOURCE]: {
		key: LEAD_FILTERS_KEYS.SOURCE,
		label: 'Source',
		type: 'String'
	}
};
